// Dane: α, β i bok a (naprzeciw α). Oblicz b i c.
// γ = 180° - α - β;  b = a * sinβ/sinα;  c = a * sinγ/sinα.

import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import RootFractionInput, {
  parseRootFractionInput,
  formatLatexPreview,
} from "../../../../components/inputs/RootFractionInput";

/* =======================  HELPERY  ======================= */

function sinLatexWithApprox(angle) {
  switch (angle) {
    case 30:
    case 150:
      return { latex: "\\dfrac{1}{2}", approx: false };
    case 45:
    case 135:
      return { latex: "\\dfrac{\\sqrt{2}}{2}", approx: false };
    case 60:
    case 120:
      return { latex: "\\dfrac{\\sqrt{3}}{2}", approx: false };
    case 90:
      return { latex: "1", approx: false };
    default:
      const val = Math.sin((angle * Math.PI) / 180).toFixed(3);
      return { latex: val.replace(".", "{,}"), approx: true };
  }
}


// LaTeX dla „tabelkowych” sinusów
function sinLatex(angle) {
  switch (angle) {
    case 30:
    case 150:
      return "\\dfrac{1}{2}";
    case 45:
    case 135:
      return "\\dfrac{\\sqrt{2}}{2}";
    case 60:
    case 120:
      return "\\dfrac{\\sqrt{3}}{2}";
    case 90:
      return "1";
    default:
      return ""; // pozostałe kąty: użyjemy przybliżenia
  }
}

// wartość liczbowo
function sinNumeric(angle) {
  switch (angle) {
    case 30:
    case 150:
      return 0.5;
    case 45:
    case 135:
      return Math.SQRT2 / 2;
    case 60:
    case 120:
      return Math.sqrt(3) / 2;
    case 90:
      return 1;
    default:
      return Math.sin((angle * Math.PI) / 180);
  }
}

// kategorie sinusów z tabeli
function sinTag(angle) {
  if (angle === 30 || angle === 150) return "H";   // 1/2
  if (angle === 45 || angle === 135) return "R2";  // √2/2
  if (angle === 60 || angle === 120) return "R3";  // √3/2
  if (angle === 90) return "ONE";                  // 1
  return null; // poza tabelą
}

// gcd do skracania
function gcd(a, b) {
  a = Math.abs(a); b = Math.abs(b);
  while (b) { const t = a % b; a = b; b = t; }
  return a || 1;
}

// format dziesiętny do LaTeX z przecinkiem, z obcięciem zer
function formatDecimalLatex(x, digits = 2) {
  const s = x.toFixed(digits);
  // usuń zbędne zera i kropkę
  const trimmed = s.replace(/(\.\d*?[1-9])0+$/,"$1").replace(/\.0+$/,"");
  return trimmed.replace(".", "{,}");
}

// PREVIEW: liczba mieszana / dziesiętna (dla podglądu w polu)
function formatLatexPreviewMixed(raw) {
  const s = String(raw ?? "").trim();
  const m = s.match(/^\s*([+-]?\d+)\s+(\d+)\s*\/\s*(\d+)\s*$/); // A B/C
  if (m) {
    const A = m[1], B = m[2], C = m[3];
    return `${A}\\;\\dfrac{${B}}{${C}}`;
  }
  if (/^[+-]?\d+[.,]\d+$/.test(s.replace(/\s+/g, ""))) {
    return s.replace(/\s+/g, "").replace(".", "{,}");
  }
  return formatLatexPreview ? formatLatexPreview(s) : s;
}

// pre-proces dla parsera: mieszane i przecinek
// pre-proces dla parsera: mieszane i przecinek
function preprocessRaw(raw) {
  if (raw == null) return "";
  let s = String(raw).trim();

  // liczba mieszana A B/C -> ułamek niewłaściwy
  const m = s.match(/^\s*([+-]?\d+)\s+(\d+)\s*\/\s*(\d+)\s*$/);
  if (m) {
    const A = parseInt(m[1], 10), B = parseInt(m[2], 10), C = parseInt(m[3], 10);
    const improper = A >= 0 ? (A * C + B) : (A * C - B);
    return `${improper}/${C}`;
  }

  // normalizacja: minus, spacje, przecinek
  s = s.replace(/\u2212/g, "-");
  s = s.replace(/\s+/g, "").replace(",", ".");

  // ✅ unicode √... -> sqrt(...)
  // √2, √3, √12, √(2), √(3/2) itd.
  s = s
    .replace(/√\(([^)]+)\)/g, "sqrt($1)")
    .replace(/√([0-9]+(?:\.[0-9]+)?)/g, "sqrt($1)");

  // (opcjonalnie, ale często pomaga) \sqrt{...} -> sqrt(...)
  s = s.replace(/\\sqrt\{([^}]+)\}/g, "sqrt($1)");

  // ✅ kluczowe: wstaw '*' między cyfrą a sqrt / \sqrt / '('
  // (żeby 7.5sqrt(2) stało się 7.5*sqrt(2))
  s = s.replace(/(\d)(?=(√|\\sqrt|sqrt|\())/g, "$1*");

  return s;
}

/* --------- dokładny zapis K * (sinTag(num) / sinTag(den)) ---------
   Zwrot: LaTeX z racjonalizacją (np. a√2, (a√6)/3, a/2, itp.)
-------------------------------------------------------------------*/
function latexExactKTimesRatio(K, numTag, denTag) {
  // mapa wyniku dla samego stosunku sinów (bez K):
  // zwracamy {sqrt: null|'2'|'3'|'6', num: licznik, den: mianownik}
  function ratioComp(n, d) {
    const key = `${n}/${d}`;
    switch (key) {
      case "H/H":   return { sqrt: null, num: 1, den: 1 };
      case "H/R2":  return { sqrt: "2", num: 1, den: 2 }; // 1/√2
      case "H/R3":  return { sqrt: "3", num: 1, den: 3 }; // 1/√3
      case "H/ONE": return { sqrt: null, num: 1, den: 2 };

      case "R2/H":  return { sqrt: "2", num: 1, den: 1 };
      case "R2/R2": return { sqrt: null, num: 1, den: 1 };
      case "R2/R3": return { sqrt: "6", num: 1, den: 3 }; // √2/√3 = √6/3
      case "R2/ONE":return { sqrt: "2", num: 1, den: 2 };

      case "R3/H":  return { sqrt: "3", num: 1, den: 1 };
      case "R3/R2": return { sqrt: "6", num: 1, den: 2 }; // √3/√2 = √6/2
      case "R3/R3": return { sqrt: null, num: 1, den: 1 };
      case "R3/ONE":return { sqrt: "3", num: 1, den: 2 };

      case "ONE/H":   return { sqrt: null, num: 2, den: 1 };
      case "ONE/R2":  return { sqrt: "2", num: 1, den: 1 }; // 2/√2 = √2
      case "ONE/R3":  return { sqrt: "3", num: 2, den: 3 }; // 2/√3 = 2√3/3
      case "ONE/ONE": return { sqrt: null, num: 1, den: 1 };
      default:
        return null;
    }
  }

  const comp = ratioComp(numTag, denTag);
  if (!comp) return ""; // nie powinno się zdarzyć

  let num = K * comp.num;
  const den = comp.den;
  const sqrt = comp.sqrt;

  if (!sqrt) {
    // czysto liczbowo: K * (num/den)
    const g = gcd(num, den);
    num /= g;
    const denS = den / g;
    return denS === 1 ? `${num}` : `\\dfrac{${num}}{${denS}}`;
  }

  // mamy postać: (num * √sqrt) / den
  if (den === 1) return `${num}\\sqrt{${sqrt}}`;
  if (num % den === 0) return `${num / den}\\sqrt{${sqrt}}`;
  return `\\dfrac{${num}\\sqrt{${sqrt}}}{${den}}`;
}

/* =======================  GENERATOR  ======================= */

export function generateTask() {
  // kąty do losowania — BEZ 90°, żeby nie generować trójkątów prostokątnych
  const options = [30, 45, 60, 120, 135, 150];

  let alpha, beta, gamma;
  do {
    alpha = options[Math.floor(Math.random() * options.length)];
    beta  = options[Math.floor(Math.random() * options.length)];
    gamma = 180 - alpha - beta;
    // warunki:
    // - poprawny trójkąt (α + β < 180)
    // - brak kąta prostego (γ ≠ 90, a w puli i tak nie ma 90 dla α, β)
  } while (alpha + beta >= 180 || gamma === 90);

  const a = Math.floor(Math.random() * 13) + 3; // 3..15

  return {
    type: "FindSidesFromTwoAnglesAndOneSideTask",
    data: { type: "FindSidesFromTwoAnglesAndOneSideTask", a, alpha, beta, gamma },
  };
}


/* =======================  RENDER: TREVŚĆ  ======================= */

export function renderTask({ data }) {
  const { a, alpha, beta } = data;
  return (
    <div style={{ marginTop: "6px", marginBottom: "6px" }}>
      <div>
        W trójkącie <InlineMath math={'ABC'} /> dane są kąty{" "}
        <InlineMath math={`\\alpha=${alpha}^\\circ`} /> i{" "}
        <InlineMath math={`\\beta=${beta}^\\circ`} /> oraz bok{" "}
        <InlineMath math={`a=${a}`} />. Oblicz <InlineMath math={'b'} /> i{" "}
        <InlineMath math={'c'} />.
      </div>

      <div style={{ marginTop: 10, opacity: 0.92 }}>
        Wyniki możesz podać <strong>dokładnie</strong> (np. z pierwiastkiem) albo{" "}
        <strong>w przybliżeniu</strong> do dwóch miejsc po przecinku.
      </div>
    </div>
  );
}

/* =======================  INPUT  ======================= */

export function renderInput({ data }, userAnswer, onChange, disabled) {
  const val = userAnswer ?? { b: { raw: "" }, c: { raw: "" } };

  const groupStyle = { display: "grid", gap: "6px" };

  return (
    <div style={{ display: "grid", gap: "14px", marginTop: "6px" }}>
      <div style={groupStyle}>
        <InlineMath math={'b ='} />
        <RootFractionInput
          value={val.b}
          onChange={(v) => onChange({ ...(userAnswer ?? {}), b: v })}
          disabled={disabled}
          label="b"                         // <-- to zasila „Podgląd: b = …”
          formatLatexPreview={formatLatexPreviewMixed}
        />
      </div>

      <div style={groupStyle}>
        <InlineMath math={'c ='} />
        <RootFractionInput
          value={val.c}
          onChange={(v) => onChange({ ...(userAnswer ?? {}), c: v })}
          disabled={disabled}
          label="c"                         // <-- i tu „Podgląd: c = …”
          formatLatexPreview={formatLatexPreviewMixed}
        />
      </div>
    </div>
  );
}


/* =======================  CHECK  ======================= */

export function checkAnswer({ data }, userAnswer) {
  const { a, alpha, beta, gamma } = data;

  const bExp = a * (sinNumeric(beta) / sinNumeric(alpha));
  const cExp = a * (sinNumeric(gamma) / sinNumeric(alpha));

  const bRaw = typeof userAnswer?.b === "object" ? userAnswer?.b?.raw : userAnswer?.b;
  const cRaw = typeof userAnswer?.c === "object" ? userAnswer?.c?.raw : userAnswer?.c;

  const bParsed = parseRootFractionInput(preprocessRaw(bRaw));
  const cParsed = parseRootFractionInput(preprocessRaw(cRaw));

  // tolerancja do akceptacji przybliżeń (2 miejsca po przecinku)
  const tol = 0.01;

  const okB = bParsed != null && Number.isFinite(bParsed) && Math.abs(bParsed - bExp) <= tol;
  const okC = cParsed != null && Number.isFinite(cParsed) && Math.abs(cParsed - cExp) <= tol;

  const parts = { b: okB, c: okC };
  const score = (okB ? 1 : 0) + (okC ? 1 : 0);

  return {
    isCorrect: score === 2,
    score,
    maxScore: 2,
    parts,
  };
}

/* =======================  ANSWER  ======================= */
/* Krótka odpowiedź:
   - jeśli oba kąty we wzorze są tabelkowe → zapis dokładny,
   - w przeciwnym wypadku → przybliżenie (2 miejsca). */

export function renderAnswer({ data }) {
  const { a, alpha, beta, gamma } = data;

  const tagA = sinTag(alpha);
  const tagB = sinTag(beta);
  const tagG = sinTag(gamma);

  // b
  const bIsExact = !!(tagB && tagA);
  const bText = bIsExact
    ? latexExactKTimesRatio(a, tagB, tagA)
    : formatDecimalLatex(a * (sinNumeric(beta) / sinNumeric(alpha)), 2);

  // c
  const cIsExact = !!(tagG && tagA);
  const cText = cIsExact
    ? latexExactKTimesRatio(a, tagG, tagA)
    : formatDecimalLatex(a * (sinNumeric(gamma) / sinNumeric(alpha)), 2);

  return (
    <div style={{ marginTop: "6px", marginBottom: "6px" }}>
      <InlineMath math={`b ${bIsExact ? "=" : "\\approx"} ${bText},\\quad`} />
      <InlineMath math={`c ${cIsExact ? "=" : "\\approx"} ${cText}`} />
    </div>
  );
}


/* =======================  EXPLANATION  ======================= */

export function renderExplanation({ data }) {
  const { a, alpha, beta, gamma } = data;

  // Kąty: wartości sinusów + info czy to przybliżenie
  const sA = sinLatexWithApprox(alpha);
  const sB = sinLatexWithApprox(beta);
  const sG = sinLatexWithApprox(gamma);

  // "tabelkowe" tagi
  const tagA = sinTag(alpha);
  const tagB = sinTag(beta);
  const tagG = sinTag(gamma);

  const bIsExact = !!(tagB && tagA);
  const cIsExact = !!(tagG && tagA);

  // odpowiedź skrócona
  const bText = bIsExact
    ? latexExactKTimesRatio(a, tagB, tagA)
    : formatDecimalLatex(a * (sinNumeric(beta) / sinNumeric(alpha)), 2);

  const cText = cIsExact
    ? latexExactKTimesRatio(a, tagG, tagA)
    : formatDecimalLatex(a * (sinNumeric(gamma) / sinNumeric(alpha)), 2);

  // liczby do wersji przybliżonej (3 miejsca dla sinów/ilorazu, 2 dla wyniku)
  const sA3 = formatDecimalLatex(sinNumeric(alpha), 3);
  const sB3 = formatDecimalLatex(sinNumeric(beta), 3);
  const sG3 = formatDecimalLatex(sinNumeric(gamma), 3);
  const ratioB3 = formatDecimalLatex(sinNumeric(beta) / sinNumeric(alpha), 3);
  const ratioG3 = formatDecimalLatex(sinNumeric(gamma) / sinNumeric(alpha), 3);
  const resB2 = formatDecimalLatex(a * (sinNumeric(beta) / sinNumeric(alpha)), 2);
  const resC2 = formatDecimalLatex(a * (sinNumeric(gamma) / sinNumeric(alpha)), 2);

  // helper: zmniejsz ułamki wewnątrz (dfrac -> tfrac)
  const toTfrac = (latex) => latex.replace(/\\dfrac/g, "\\tfrac");

  return (
    <div style={{ textAlign: "left", marginTop: "8px", lineHeight: 1.7 }}>
      <div><strong>Krok 1.</strong> Trzeci kąt:</div>
      <BlockMath math={`\\gamma = 180^\\circ - ${alpha}^\\circ - ${beta}^\\circ = ${gamma}^\\circ`} />

      <div><strong>Krok 2.</strong> Twierdzenie sinusów:</div>
      <BlockMath math={`\\dfrac{a}{\\sin\\alpha}=\\dfrac{b}{\\sin\\beta}=\\dfrac{c}{\\sin\\gamma}`} />

      <div><strong>Krok 3.</strong> Wzory na b i c:</div>
      <BlockMath math={`b = a\\cdot\\dfrac{\\sin\\beta}{\\sin\\alpha},\\quad c = a\\cdot\\dfrac{\\sin\\gamma}{\\sin\\alpha}`} />

      <div><strong>Krok 4.</strong> Podstawiamy dane:</div>
      <BlockMath math={`b = ${a}\\cdot\\dfrac{\\sin(${beta}^\\circ)}{\\sin(${alpha}^\\circ)},\\quad c = ${a}\\cdot\\dfrac{\\sin(${gamma}^\\circ)}{\\sin(${alpha}^\\circ)}`} />

      <div><strong>Krok 5.</strong> Wartości sinusów:</div>
      <BlockMath math={`\\sin(${alpha}^\\circ) ${sA.approx ? "\\approx" : "="} ${sA.latex}`} />
      <BlockMath math={`\\sin(${beta}^\\circ) ${sB.approx ? "\\approx" : "="} ${sB.latex}`} />
      <BlockMath math={`\\sin(${gamma}^\\circ) ${sG.approx ? "\\approx" : "="} ${sG.latex}`} />

      <div><strong>Krok 6.</strong> Podstawiamy do wzoru i liczymy krok po kroku:</div>

      {/* b: jedna linia */}
      {bIsExact ? (
        <BlockMath math={`b = ${a}\\cdot\\dfrac{\\sin(${beta}^\\circ)}{\\sin(${alpha}^\\circ)} = ${a}\\cdot\\dfrac{${toTfrac(sinLatex(beta))}}{${toTfrac(sinLatex(alpha))}} = ${bText}`} />
      ) : (
        <BlockMath math={`b = ${a}\\cdot\\dfrac{\\sin(${beta}^\\circ)}{\\sin(${alpha}^\\circ)} \\approx ${a}\\cdot\\dfrac{${sB3}}{${sA3}} \\approx ${a}\\cdot ${ratioB3} \\approx ${resB2}`} />
      )}

      {/* c: jedna linia */}
      {cIsExact ? (
        <BlockMath math={`c = ${a}\\cdot\\dfrac{\\sin(${gamma}^\\circ)}{\\sin(${alpha}^\\circ)} = ${a}\\cdot\\dfrac{${toTfrac(sinLatex(gamma))}}{${toTfrac(sinLatex(alpha))}} = ${cText}`} />
      ) : (
        <BlockMath math={`c = ${a}\\cdot\\dfrac{\\sin(${gamma}^\\circ)}{\\sin(${alpha}^\\circ)} \\approx ${a}\\cdot\\dfrac{${sG3}}{${sA3}} \\approx ${a}\\cdot ${ratioG3} \\approx ${resC2}`} />
      )}

      <div style={{ marginTop: "6px" }}>
        <strong>Odpowiedź:</strong>
        <BlockMath math={`b ${bIsExact ? "=" : "\\approx"} ${bText},\\quad c ${cIsExact ? "=" : "\\approx"} ${cText}`} />
      </div>
    </div>
  );
}




/* =======================  USER ANSWER  ======================= */

function formatRootFracLatex(raw) {
  let s = String(raw ?? "").trim();
  if (!s) return "";

  // liczba mieszana A B/C
  const m = s.match(/^\s*([+-]?\d+)\s+(\d+)\/(\d+)\s*$/);
  if (m) return `${m[1]}\\;\\dfrac{${m[2]}}{${m[3]}}`;

  // dziesiętna
  if (/^[+-]?\d+[.,]\d+$/.test(s.replace(/\s+/g, ""))) {
    return s.replace(/\s+/g, "").replace(".", "{,}");
  }

  // ułamki/pierwiastki
  s = s
    .replace(/\u2212/g, "-")
    .replace(/\s+/g, "")
    .replace(/sqrt\(([^)]+)\)/g, "\\sqrt{$1}")
    .replace(/\*\s*(?=\\sqrt|[a-zA-Z])/g, "");
  const slash = s.indexOf("/");
  if (slash > -1) {
    const num = s.slice(0, slash);
    const den = s.slice(slash + 1);
    return `\\dfrac{${num}}{${den}}`;
  }
  return s;
}

export function renderUserAnswer(userAnswer) {
  if (!userAnswer) return <i>(brak odpowiedzi)</i>;
  const bRaw = typeof userAnswer.b === "object" ? userAnswer.b.raw : userAnswer.b;
  const cRaw = typeof userAnswer.c === "object" ? userAnswer.c.raw : userAnswer.c;

  const bLatex = formatRootFracLatex(bRaw);
  const cLatex = formatRootFracLatex(cRaw);

  return (
    <div style={{ marginTop: "6px", marginBottom: "6px" }}>
      <InlineMath math={`\\displaystyle b = ${bLatex || '?'}`} />
      <span style={{ margin: "0 8px" }} />
      <InlineMath math={`\\displaystyle c = ${cLatex || '?'}`} />
    </div>
  );
}

export default {
  generateTask,
  renderTask,
  renderInput,
  checkAnswer,
  renderAnswer,
  renderExplanation,
  renderUserAnswer,
};
